import type {
  ApInvoice,
  ApInvoiceStatus,
  ApJobSearchResult,
  ApProjectSearchResult,
} from "@/accounts-payable/types";
import { extractErrorMessage } from "@/core/api/errors";
import { formatApDate, formatDollars } from "./format";
import { isNonJobExpense } from "./status-meta";

export const MIN_JOB_SEARCH_LENGTH = 3;

export const JOB_SEARCH_LIMIT = 20;

const MANUAL_JOB_MATCH_STATUSES: ApInvoiceStatus[] = [
  "parsed",
  "matched",
  "match_failed",
];

export const canManuallyMatchJob = (
  invoice: Pick<ApInvoice, "status">,
): boolean => MANUAL_JOB_MATCH_STATUSES.includes(invoice.status);

export const needsJob = (invoice: {
  status: ApInvoiceStatus;
  st_job_external_id?: number | null;
  st_project_external_id?: number | null;
}): boolean =>
  canManuallyMatchJob(invoice) &&
  !invoice.st_job_external_id &&
  !invoice.st_project_external_id &&
  !isNonJobExpense(invoice);

export const jobSearchMeta = (job: ApJobSearchResult): string[] =>
  [
    job.status,
    job.completed_on ? formatApDate(job.completed_on) : null,
    job.business_unit,
    job.total === null ? null : formatDollars(job.total),
  ].filter((part): part is string => Boolean(part));

export const jobSearchStatusMessage = ({
  query,
  isLoading,
  isError,
  isRateLimited = false,
  resultCount,
  noun = "jobs",
}: {
  query: string;
  isLoading: boolean;
  isError: boolean;
  isRateLimited?: boolean;
  resultCount: number;
  noun?: "jobs" | "projects";
}): string | null => {
  if (query.trim().length < MIN_JOB_SEARCH_LENGTH)
    return `Type at least ${MIN_JOB_SEARCH_LENGTH} characters to search ${noun}.`;
  if (isLoading) return `Searching ${noun}…`;
  if (isRateLimited)
    return "ServiceTitan is busy right now. Wait a moment and search again.";
  if (isError) return `Couldn’t search ServiceTitan ${noun}. Please try again.`;
  if (resultCount === 0) return `No ${noun} match that search.`;
  return null;
};

export const projectSearchMeta = (project: ApProjectSearchResult): string[] =>
  [project.status, ...project.business_units].filter(Boolean);

export const isJobSearchRateLimited = (error: unknown): boolean =>
  error !== null && extractErrorMessage(error).status === 429;
